import { createUpload } from '@mux/upchunk';
import { nanoid } from 'nanoid';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient, useQueries } from 'react-query';

import { useFetchStorageItemInformation } from 'src/Hooks/Mutations/useFetchStorageItemInformation';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { assetStorageApi, videosApi } from 'src/Services/API';
import { FileInfoDto, ReceiptDataDto, VideoDto } from 'src/Services/API/generated';
import { trackDocumentUploaded } from 'src/Services/Mixpanel/events';

export type UploadStatus = 'uploading' | 'processing' | 'ready' | 'error';

export interface UploadableFile {
  localId: string;
  file: File;
  status: UploadStatus;
  errorMessage?: string;
  videoId?: string;
  videoData?: VideoDto;
  fileData?: FileInfoDto;
  ocrData?: ReceiptDataDto;
}

interface UseFileUploadManagerOptions {
  onUploadReady?: (file: UploadableFile) => void;
}

const POLLING_INTERVAL = 2000;

export const DEFAULT_UPLOAD_FOLDER = '/Documents téléchargés/';

export const useFileUploadManager = (options?: UseFileUploadManagerOptions) => {
  const [uploads, setUploads] = useState<UploadableFile[]>([]);
  const processedLocalIds = useRef(new Set<string>());
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();
  const { mutateAsync } = useFetchStorageItemInformation();
  const getFileInfo = useCallback(
    (fileId: string) => {
      return mutateAsync(fileId);
    },
    [mutateAsync]
  );

  const updateUploadState = (localId: string, newState: Partial<UploadableFile>) => {
    setUploads(currentUploads =>
      currentUploads.map(up => (up.localId === localId ? { ...up, ...newState } : up))
    );
  };

  const isLoading = uploads.some(
    upload => upload.status === 'uploading' || upload.status === 'processing'
  );

  const uploadMutation = useMutation(
    (vars: { files: File[]; path: string; localIds: string[]; context?: string }) => {
      if (!assetId) throw new Error('Asset ID is missing');
      return assetStorageApi.assetStorageControllerUploadFiles(
        assetId,
        vars.files,
        vars.path,
        vars.context
      );
    },
    {
      onSuccess: (response, variables) => {
        const successfulFiles = response.data.files ?? [];
        const failedFiles = response.data.failures ?? [];

        variables.files.forEach((sentFile, index) => {
          const localId = variables.localIds[index];
          if (!localId) return;

          const failureInfo = failedFiles.find(f => f.filename === sentFile.name);

          if (failureInfo) {
            updateUploadState(localId, {
              status: 'error',
              errorMessage: `Failed to upload ${failureInfo}`,
            });
          } else {
            // This would not work if two files had the same name on upload, but it should not happen as we are uploading in a single path
            const successResult = successfulFiles.find(s => s.file.originalName === sentFile.name);

            if (successResult) {
              const { file: fileInfo, ocrData } = successResult;

              if (fileInfo.status === 'processing_video' && fileInfo.videoId) {
                updateUploadState(localId, {
                  status: 'processing',
                  videoId: fileInfo.videoId,
                });
              } else {
                const finalState = {
                  status: 'ready' as const,
                  fileData: fileInfo,
                  ocrData: ocrData,
                };
                updateUploadState(localId, finalState);

                const readyFileForCallback: UploadableFile = {
                  localId,
                  file: sentFile,
                  ...finalState,
                };
                trackDocumentUploaded({
                  file_type: fileInfo.type,
                  file_size: sentFile.size,
                });
                options?.onUploadReady?.(readyFileForCallback);

                // TODO : invalidate only for the uploaded path
                queryClient.invalidateQueries({ queryKey: ['fetch-documents', assetId] });
                // Starts polling the background OCR/filing analysis of this upload
                queryClient.invalidateQueries({ queryKey: ['document-suggestions', assetId] });
              }
            } else {
              updateUploadState(localId, {
                status: 'error',
                errorMessage: 'Unknown error',
              });
            }
          }
        });
      },
    }
  );

  const startUpload = useCallback(
    async (files: FileList | File[], path: string = DEFAULT_UPLOAD_FOLDER, context?: string) => {
      const filesArray = Array.from(files);

      const newUploads: UploadableFile[] = filesArray.map(file => ({
        localId: nanoid(),
        file,
        status: 'uploading',
      }));
      setUploads(current => [...current, ...newUploads]);

      // Separate videos from other files
      const videoUploads = newUploads.filter(up => up.file.type.startsWith('video/'));
      const otherUploads = newUploads.filter(up => !up.file.type.startsWith('video/'));

      // Handle video uploads individually
      videoUploads.forEach(async videoUpload => {
        try {
          if (!assetId) throw new Error('Asset ID is missing');
          const { data: uploadConfig } = await videosApi.videosControllerCreateDirectUploadUrl(
            assetId,
            {
              filename: videoUpload.file.name,
              path,
            }
          );

          const upload = createUpload({
            endpoint: uploadConfig.uploadUrl,
            file: videoUpload.file,
            chunkSize: 5120,
          });

          upload.on('error', err => {
            console.error('Video upload failed:', err.detail);
            updateUploadState(videoUpload.localId, {
              status: 'error',
              errorMessage: 'Failed to upload video.',
            });
          });

          upload.on('success', () => {
            updateUploadState(videoUpload.localId, {
              status: 'processing',
              videoId: uploadConfig.id,
            });
          });
        } catch (error) {
          console.error('Video upload initialization failed:', error);
          updateUploadState(videoUpload.localId, {
            status: 'error',
            errorMessage: 'Failed to initialize video upload.',
          });
        }
      });

      if (otherUploads.length > 0) {
        uploadMutation.mutate({
          files: otherUploads.map(u => u.file),
          path,
          localIds: otherUploads.map(up => up.localId),
          context,
        });
      }
    },
    [uploadMutation, assetId]
  );

  const removeUpload = (localId: string) => {
    processedLocalIds.current.delete(localId);
    setUploads(current => current.filter(up => up.localId !== localId));
  };

  const processingVideos = useMemo(
    () => uploads.filter(up => up.status === 'processing' && up.videoId),
    [uploads]
  );

  const videoStatusQueries = useQueries(
    processingVideos.map(video => ({
      queryKey: ['video-status', video.videoId],
      queryFn: async () => {
        if (!assetId) {
          throw new Error('No asset selected');
        }
        return await videosApi.videosControllerFindOne(assetId, video.videoId!);
      },
      enabled: video.status === 'processing',
      refetchInterval: POLLING_INTERVAL,
    }))
  );

  useEffect(() => {
    videoStatusQueries.forEach(async (queryResult, index) => {
      const video = processingVideos[index];

      if (!video || video.status !== 'processing' || !queryResult.data) {
        return;
      }

      const videoStatusData = queryResult.data.data;

      if (videoStatusData.assetStatus === 'ready') {
        if (processedLocalIds.current.has(video.localId)) return;
        processedLocalIds.current.add(video.localId);

        if (!videoStatusData.fileId) {
          console.error('videoStatusData.fileId is missing');
          updateUploadState(video.localId, {
            status: 'error',
            errorMessage: 'File ID is missing after processing.',
          });
          return;
        }

        try {
          const fileData = await getFileInfo(videoStatusData.fileId);
          const finalState: UploadableFile = {
            localId: video.localId,
            file: video.file,
            status: 'ready' as const,
            videoId: video.videoId,
            videoData: videoStatusData,
            fileData,
          };

          updateUploadState(video.localId, finalState);
          trackDocumentUploaded({
            file_type: 'video',
            file_size: video.file.size,
          });
          options?.onUploadReady?.(finalState);
          queryClient.invalidateQueries({ queryKey: ['fetch-documents', assetId] });
        } catch (error) {
          console.error(`[ERROR] Failed to get file info for video ${video.localId}:`, error);
          updateUploadState(video.localId, {
            status: 'error',
            errorMessage: 'Failed to retrieve file information.',
          });
        }
      } else if (videoStatusData.assetStatus === 'errored') {
        updateUploadState(video.localId, {
          status: 'error',
          errorMessage: 'Video processing failed on the server.',
        });
      }
    });
  }, [
    JSON.stringify(videoStatusQueries.map(q => q.data?.data.assetStatus)),
    processingVideos,
    getFileInfo,
    assetId,
    queryClient,
    options?.onUploadReady,
  ]);

  return { uploads, startUpload, removeUpload, isLoading };
};
